import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Avatar,
  Typography,
  message,
  Popconfirm,
  Row,
  Col,
} from 'antd';
import {
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  LikeOutlined,
} from '@ant-design/icons';
import commentsService, { Comment } from '../services/commentsService';
import { getImageUrl } from '../config/api';

const { Title, Text } = Typography;

const userName = (u?: { name?: string; username?: string } | string | null): string => {
  if (!u) return 'Unknown';
  if (typeof u === 'string') return u;
  return u.name || u.username || 'Unknown';
};

const Comments: React.FC = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const { comments: list, pagination } = await commentsService.getAllComments({ page, limit: pageSize });
      setComments(list);
      setTotal(pagination.totalItems ?? (pagination.totalPages ? pagination.totalPages * pageSize : list.length));
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to fetch comments');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleDelete = async (id: string) => {
    try {
      await commentsService.deleteComment(id);
      message.success('Comment deleted');
      fetchComments();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to delete comment');
    }
  };

  const columns = [
    {
      title: 'Author',
      key: 'user',
      width: 200,
      render: (record: Comment) => {
        const u: any = record.user;
        return (
          <Space>
            <Avatar
              size="small"
              src={typeof u === 'object' && u?.profileImage ? getImageUrl(u.profileImage) : undefined}
              icon={<UserOutlined />}
            />
            <div>
              <div>{userName(u)}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {typeof u === 'object' ? u?.email || '' : ''}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Comment',
      dataIndex: 'text',
      key: 'text',
      render: (text: string, record: Comment) => (
        <Space direction="vertical" size={0}>
          <Text>{text}</Text>
          {record.parentComment ? <Tag color="geekblue">Reply</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'On Post',
      key: 'post',
      width: 200,
      render: (record: Comment) => {
        const p: any = record.post;
        const caption = typeof p === 'object' ? p?.caption : '';
        return <Text type="secondary" style={{ fontSize: 12 }}>{caption || '—'}</Text>;
      },
      ellipsis: true,
    },
    {
      title: 'Likes',
      dataIndex: 'likesCount',
      key: 'likesCount',
      width: 80,
      render: (v?: number) => (
        <Space size={4}>
          <LikeOutlined />
          {v ?? 0}
        </Space>
      ),
    },
    {
      title: 'Posted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (d: string) => (d ? new Date(d).toLocaleString() : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (record: Comment) => (
        <Popconfirm
          title="Delete this comment?"
          description="This cannot be undone."
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => handleDelete(record._id)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Comments
          </Title>
          <Text type="secondary">Moderate comments across all posts — remove spam or abusive content.</Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchComments}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns as any}
          dataSource={comments}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `${t} comments`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
};

export default Comments;
